import { EmojiText } from '@gagovictor/shared-frontend-core/dist/shared/components/Emoji';
import { Box, Chip, Stack, useMediaQuery } from '@mui/material';
import { alpha, keyframes, useTheme } from '@mui/material/styles';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useT } from '../i18n';
import { createAdPolicy } from '../monetization/adPolicy';
import { useAds } from '../monetization/AdsProvider';
import { isNative } from '../monetization/config';
import { RootState } from '../store';

import { playAlarmSound } from './alarmSounds';
import { sound } from './audio';
import {
    createTicker,
    isPageHidden,
    keepScreenOn,
    notifyDone,
    requestNotificationPermission,
    START_NEXT_MESSAGE,
    whenVisible,
} from './background';
import { backgroundCss, backgroundSize, isDarkScene } from './backgrounds';
import { CompletionId } from './catalog';
import {
    CustomizeDrawer,
    DRAWER_WIDTH,
    SHEET_HEIGHT_VH,
} from './components/CustomizeDrawer';
import { SettingsDialog } from './components/SettingsDialog';
import { StreakDialog } from './components/StreakDialog';
import { StreakRecoveryDialog } from './components/StreakRecoveryDialog';
import { TimerPanel } from './components/TimerPanel';
import { playCue } from './feedback';
import { unlockedLook } from './goals';
import { MotionHitDetector } from './motion';
import { MusicListener } from './music';
import {
    cancelNativeAlarm,
    consumeNativeAction,
    NativeProgress,
    NativeStep,
    hideRunningNotification,
    onRunningNotificationCancel,
    showRunningNotification,
    ensureFullScreenAllowed,
    ensureNativePermission,
    scheduleNativeAlarm,
} from './nativeAlarm';
import { effectiveStreak, streakStatus } from './streak';
import {
    advance,
    initialTimer,
    msFor,
    Mode,
    pause,
    reset,
    selectMode,
    skipToLast,
    start,
    tick,
    TimerState,
    formatClock,
    joinNativeTimer,
    upcomingTimers,
} from './timer';
import { TomatoCanvas, TomatoHandle } from './TomatoCanvas';
import {
    dayKey,
    focusCompleted,
    streakRecovered,
    streakRecoveryDeclined,
} from './tomatoSlice';
import { useStreakReminders } from './useStreakReminders';

const floatUp = keyframes`
    0% { opacity: 0; transform: translate(-50%, 20px) scale(.6); }
    15% { opacity: 1; transform: translate(-50%, 0) scale(1.15); }
    100% { opacity: 0; transform: translate(-50%, -90px) scale(1); }
`;

export interface PomodoroPageProps {
    drawerOpen: boolean;
    onCloseDrawer: () => void;
    /** True when a footer ad sits at the bottom, so the sheet is lifted above it instead of hiding it. */
    footerOffset: boolean;
    /** True when the phone app's banner overlays the bottom edge, so the controls sit above it. */
    nativeBanner?: boolean;
    settingsOpen: boolean;
    onCloseSettings: () => void;
}

export const PomodoroPage: React.FC<PomodoroPageProps> = ({
    drawerOpen,
    onCloseDrawer,
    footerOffset,
    nativeBanner = false,
    settingsOpen,
    onCloseSettings,
}) => {
    const theme = useTheme();
    const mobile = useMediaQuery(theme.breakpoints.down('md'));
    const dispatch = useDispatch();
    const t = useT();
    const {
        look: savedLook,
        completion,
        durations,
        muted,
        alarmSound,
        lockScreenAlarm,
        stats,
        streakReminders,
    } = useSelector((s: RootState) => s.tomato);
    // An item whose community goal is not funded yet stays off, even in a look saved before it was gated.
    const look = useMemo(() => unlockedLook(savedLook), [savedLook]);
    const { adsEnabled, showAdBreak, showRewardedBreak } = useAds();
    useStreakReminders(stats, streakReminders);
    const statsRef = useRef(stats);
    statsRef.current = stats;
    const [streakOpen, setStreakOpen] = useState(false);
    const [giftBusy, setGiftBusy] = useState(false);
    const [giftRetry, setGiftRetry] = useState(false);
    const today = dayKey(new Date());
    const shownStreak = effectiveStreak(stats, today);

    const [timer, setTimer] = useState<TimerState>(() =>
        initialTimer(durations)
    );
    const timerRef = useRef(timer);
    const durationsRef = useRef(durations);
    durationsRef.current = durations;
    const lockScreenAlarmRef = useRef(lockScreenAlarm);
    lockScreenAlarmRef.current = lockScreenAlarm;
    timerRef.current = timer;
    const [celebrating, setCelebrating] = useState(false);
    const [popups, setPopups] = useState<{ id: number; text: string }[]>([]);
    const tomato = useRef<TomatoHandle>(null);
    const [listening, setListening] = useState(false);
    const [listenFailed, setListenFailed] = useState(false);
    const listener = useRef<MusicListener | null>(null);
    const policy = useRef(createAdPolicy()).current;
    const policyRef = useRef(policy);
    const showAdBreakRef = useRef(showAdBreak);
    showAdBreakRef.current = showAdBreak;

    useEffect(() => {
        sound.muted = muted;
    }, [muted]);

    // Dark mode gets a spooky "Oouuuuh", light mode a yawn. Not on first load, only when it changes.
    const dark = theme.palette.mode === 'dark';
    const lastDark = useRef(dark);
    useEffect(() => {
        if (lastDark.current === dark) return;
        lastDark.current = dark;
        if (dark) {
            playCue('ooh');
            tomato.current?.spook();
        } else {
            playCue('yawn');
            tomato.current?.yawn();
        }
    }, [dark]);

    const toggleListening = useCallback(async () => {
        if (listener.current?.active) {
            listener.current.stop();
            setListening(false);
            return;
        }
        listener.current ??= new MusicListener();
        setListenFailed(false);
        const ok = await listener.current.start((f) =>
            tomato.current?.music(f.level, f.beat)
        );
        setListening(ok);
        setListenFailed(!ok);
    }, []);
    useEffect(() => () => listener.current?.stop(), []);

    // Bat the phone like a ping-pong racket and the tomato jumps. iOS only grants motion access
    // from a gesture, so the first tap anywhere starts it.
    useEffect(() => {
        const detector = new MotionHitDetector((strength) => {
            tomato.current?.bounce(strength);
            playCue('bounce', 0.8 + strength * 0.6);
        });
        const arm = () => void detector.start();
        window.addEventListener('pointerdown', arm, { once: true });
        return () => {
            window.removeEventListener('pointerdown', arm);
            detector.stop();
        };
    }, []);

    // Changing a duration in the settings re-arms an idle timer; a running one is left alone.
    useEffect(() => {
        setTimer((t) =>
            t.status === 'idle' ? selectMode(t, t.mode, durations) : t
        );
    }, [durations]);

    const total = msFor(timer.mode, durations);
    // The nap roulette hides how far along it is: nobody knows when it goes off.
    const progress =
        timer.status === 'idle' || timer.mode === 'roulette'
            ? 0
            : 1 - timer.remainingMs / total;
    const running = timer.status === 'running';

    // The last 10 seconds of a focus session: a scream from far away that gets closer until the timer
    // ends, then the happy "weee" of the finish. Stops if the timer is paused, reset or skipped.
    const screaming = useRef(false);
    const inFinalStretch =
        running &&
        timer.mode === 'focus' &&
        timer.remainingMs <= 10_000 &&
        timer.remainingMs > 300;
    useEffect(() => {
        if (inFinalStretch && !screaming.current) {
            screaming.current = true;
            sound.scream(timerRef.current.remainingMs / 1000);
        } else if (!inFinalStretch && screaming.current) {
            screaming.current = false;
            sound.stopScream();
        }
    }, [inFinalStretch]);
    useEffect(() => () => sound.stopScream(), []);

    useEffect(() => {
        document.title =
            running && timer.mode !== 'roulette'
                ? `${formatClock(timer.remainingMs)} · ${t('app.name')}`
                : t('app.name');
    }, [running, timer.mode, timer.remainingMs, t]);

    const popup = useCallback((text: string) => {
        const id = Date.now() + Math.random();
        setPopups((p) => [...p, { id, text }]);
        window.setTimeout(
            () => setPopups((p) => p.filter((x) => x.id !== id)),
            1600
        );
    }, []);

    /** True from a finish while hidden until the page is visible again: the OS alarm must keep ringing. */
    const alarmHandedOver = useRef(false);
    /** Set while a finish waits for the page to come back: pressing "start next" in the notification ends the wait. */
    const hiddenStartNext = useRef<(() => void) | null>(null);

    const finish = useCallback(
        async (finished: TimerState) => {
            const wasFocus = finished.mode === 'focus';
            const wasRoulette = finished.mode === 'roulette';
            let skipShow = false;
            setCelebrating(true);
            setTimer({
                ...finished,
                status: 'idle',
                endsAt: null,
                remainingMs: 0,
            });
            // Count it at once: if the tab is closed while it is hidden, the session still happened.
            if (wasFocus) dispatch(focusCompleted(undefined));
            if (isPageHidden()) {
                // The OS alarm is ringing or about to: going idle below must not cancel it (see the schedule effect).
                alarmHandedOver.current = true;
                // Nobody can see the tomato (hidden tab, covered or minimized window), and nothing animates
                // without frames. Make noise and send a notification now; the show waits for their return.
                // On the phone the OS alarm is already ringing (see cancelNativeAlarm below).
                if (!isNative() && !sound.muted) playAlarmSound(alarmSound);
                void notifyDone({
                    title: t('app.name'),
                    body: t(
                        wasFocus
                            ? 'notify.focus'
                            : wasRoulette
                              ? 'notify.roulette'
                              : 'notify.break'
                    ),
                    actions: {
                        startNext: t(
                            wasFocus ? 'notify.startRest' : 'notify.restart'
                        ),
                        dismiss: t('notify.dismiss'),
                    },
                });
                // The show waits for the person's return, unless they press the notification's "start next"
                // button first: that must not need the tab in front, so the show is skipped (nobody can see it).
                await Promise.race([
                    whenVisible(),
                    new Promise<void>((resolve) => {
                        hiddenStartNext.current = () => {
                            skipShow = true;
                            resolve();
                        };
                    }),
                ]);
                hiddenStartNext.current = null;
                if (skipShow) alarmHandedOver.current = false;
            } else if (!sound.muted) {
                playAlarmSound(alarmSound);
            }
            if (skipShow) {
                // Nothing to show: just move on, and the pending "start next" begins the next timer.
            } else if (wasFocus) {
                popup(t('page.plusOne'));
                // The first session of a day that grew the streak earns a cheer (read after the render that follows the dispatch).
                const after = statsRef.current;
                if (after.today === 1 && after.streak >= 2)
                    window.setTimeout(
                        () => popup(t('streak.popup', { count: after.streak })),
                        700
                    );
                await tomato.current?.playCompletion(completion);
                if (policy.onMatchEnd() && adsEnabled) await showAdBreak();
            } else if (wasRoulette) {
                popup(t('page.rouletteBoom'));
                await tomato.current?.playCompletion(completion);
            } else {
                tomato.current?.celebrate();
                popup(t('page.breakOver'));
                await new Promise((r) => setTimeout(r, 900));
            }
            setTimer((t) =>
                advance(
                    {
                        ...t,
                        mode: finished.mode,
                        focusDone: finished.focusDone,
                    },
                    durations,
                    wasFocus
                )
            );
            setCelebrating(false);
        },
        [
            adsEnabled,
            alarmSound,
            completion,
            dispatch,
            durations,
            policy,
            popup,
            showAdBreak,
            t,
        ]
    );

    // The clock runs on a worker thread so a background tab does not slow it down, and is re-checked
    // the moment the page becomes visible again (a minimized phone app may have been frozen entirely).
    useEffect(() => {
        if (!running) return;
        let done = false;
        const onTick = () => {
            if (done) return;
            const { state, finished } = tick(timerRef.current, Date.now());
            if (finished) {
                done = true;
                ticker.stop();
                // Hidden: the OS alarm (maybe already ringing) takes over; only silence it if we are watching.
                if (!isPageHidden()) void cancelNativeAlarm();
                void finish(state);
            } else {
                // Close to the end and watching: the app handles the finish itself, so drop the OS alert.
                if (state.remainingMs <= 1500 && !isPageHidden()) {
                    void cancelNativeAlarm();
                }
                setTimer(state);
            }
        };
        const ticker = createTicker(onTick, 250);
        document.addEventListener('visibilitychange', onTick);
        return () => {
            ticker.stop();
            document.removeEventListener('visibilitychange', onTick);
        };
    }, [running, finish]);

    // Keep the screen awake while running, so a phone does not suspend the page mid-session.
    useEffect(() => {
        if (!running) return;
        const lock = keepScreenOn();
        return () => lock.release();
    }, [running]);

    // In the phone app a minimized app cannot run script: the OS is asked to alert at the end time.
    useEffect(() => {
        const sync = () => {
            if (timer.status === 'running' && timer.endsAt) {
                void scheduleNativeAlarm(timer.endsAt, {
                    title: t('app.name'),
                    body: t(
                        timer.mode === 'focus'
                            ? 'notify.focus'
                            : timer.mode === 'roulette'
                              ? 'notify.roulette'
                              : 'notify.break'
                    ),
                    sound: alarmSound,
                    overlay: lockScreenAlarm,
                    // After a focus session the alarm offers to start the rest; after any other timer, to restart.
                    actionLabel:
                        timer.mode === 'focus'
                            ? t('notify.startRest')
                            : t('notify.restart'),
                    dismissLabel: t('notify.dismiss'),
                    // So the button can start the next timers itself while the app is away.
                    next: upcomingTimers(timer, durations, 4).map(
                        (u): NativeStep => ({
                            mode: u.mode,
                            ms: u.ms,
                            focusDone: u.focusDone,
                            endsFocus: u.mode === 'focus',
                            title: t('app.name'),
                            body: t(
                                u.mode === 'focus'
                                    ? 'notify.focus'
                                    : u.mode === 'roulette'
                                      ? 'notify.roulette'
                                      : 'notify.break'
                            ),
                            actionLabel:
                                u.mode === 'focus'
                                    ? t('notify.startRest')
                                    : t('notify.restart'),
                            runningTitle: t('app.name'),
                            runningBody: t('notify.running', {
                                time: '{time}',
                            }),
                            cancelLabel: t('notify.cancel'),
                        })
                    ),
                    completesFocus: timer.mode === 'focus',
                });
            } else if (alarmHandedOver.current) {
                // The timer ended while hidden: the OS alarm owns the ringing until it is tended to or the
                // page comes back to the front.
                if (!isPageHidden()) {
                    alarmHandedOver.current = false;
                    void cancelNativeAlarm();
                }
            } else {
                void cancelNativeAlarm();
            }
        };
        sync();
        document.addEventListener('visibilitychange', sync);
        return () => document.removeEventListener('visibilitychange', sync);
    }, [
        timer.status,
        timer.endsAt,
        timer.mode,
        timer.focusDone,
        durations,
        t,
        alarmSound,
        lockScreenAlarm,
    ]);

    // Ongoing notification with a Cancel button while a timer runs (phone app only).
    useEffect(() => {
        if (timer.status === 'running' && timer.endsAt) {
            const time = new Date(timer.endsAt).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
            });
            void showRunningNotification(
                timer.endsAt,
                t('app.name'),
                t('notify.running', { time }),
                t('notify.cancel')
            );
        } else {
            void hideRunningNotification();
        }
    }, [timer.status, timer.endsAt, t]);
    useEffect(() => () => void hideRunningNotification(), []);
    useEffect(
        () =>
            onRunningNotificationCancel(() => {
                playCue('pause');
                setTimer((cur) => reset(cur, durations));
            }),
        [durations]
    );

    // "Start rest" on the alarm (notification button or lock-screen alarm): remember it, and start the next
    // timer once the finish show has advanced to it. Checked when the app comes back to the front.
    const [startNextWanted, setStartNextWanted] = useState(false);
    /** Set when the alarm button started the next timer natively; applied once the finish show is over. */
    const nativeProgress = useRef<NativeProgress | null>(null);
    // Web: the same button on the browser notification (posted by the service worker).
    useEffect(() => {
        if (!('serviceWorker' in navigator)) return;
        const onMessage = (e: MessageEvent) => {
            if (e.data?.type !== START_NEXT_MESSAGE) return;
            hiddenStartNext.current?.();
            setStartNextWanted(true);
        };
        navigator.serviceWorker.addEventListener('message', onMessage);
        return () =>
            navigator.serviceWorker.removeEventListener('message', onMessage);
    }, []);
    useEffect(() => {
        const check = () => {
            if (!document.hidden)
                void consumeNativeAction().then(({ action, progress }) => {
                    if (action === 'startNext') {
                        // The phone already started it (alarm button, app away): catch up instead of starting afresh.
                        nativeProgress.current = progress ?? null;
                        setStartNextWanted(true);
                    } else if (action === 'cancelTimer')
                        setTimer((cur) => reset(cur, durationsRef.current));
                });
        };
        check();
        document.addEventListener('visibilitychange', check);
        return () => document.removeEventListener('visibilitychange', check);
    }, []);

    // The surprise: offered once, after the finish show, on the day a streak restarts. Nothing else mentions it.
    const giftOpen =
        !celebrating &&
        stats.lostStreak !== null &&
        stats.lostStreak.day === today;
    const acceptGift = async () => {
        setGiftBusy(true);
        try {
            const result = await showRewardedBreak();
            if (result === 'dismissed') {
                setGiftRetry(true);
                return;
            }
            // Watched, or no ad could be shown: never trap the person.
            const lost = stats.lostStreak?.streak ?? 0;
            dispatch(streakRecovered(undefined));
            setGiftRetry(false);
            popup(t('streak.gift.back', { count: lost + 1 }));
            tomato.current?.celebrate();
        } finally {
            setGiftBusy(false);
        }
    };

    const toggle = useCallback(() => {
        const t = timerRef.current;
        if (t.status === 'running') {
            playCue('pause');
            setTimer(pause(t, Date.now()));
            return;
        }
        playCue('start');
        tomato.current?.hype();
        // From a tap, as browsers require: lets a finished timer reach you when the tab is hidden.
        void requestNotificationPermission();
        void ensureNativePermission().then((granted) => {
            if (granted && lockScreenAlarmRef.current)
                void ensureFullScreenAllowed();
        });
        // A new focus session may open with the ad (the coin flip also decides it may close with it).
        // The timer starts once the ad is closed, so the ad never eats focus time.
        // Never while the page is hidden (a notification button started it): nobody could see the ad.
        const beginsFocus =
            t.status === 'idle' && t.mode === 'focus' && !isPageHidden();
        if (beginsFocus && policyRef.current.onMatchStart()) {
            void showAdBreakRef
                .current()
                .then(() =>
                    setTimer((cur) =>
                        start(cur, Date.now(), durationsRef.current)
                    )
                );
            return;
        }
        setTimer((cur) => start(cur, Date.now(), durationsRef.current));
    }, []);

    useEffect(() => {
        if (!startNextWanted || timer.status !== 'idle' || celebrating) return;
        setStartNextWanted(false);
        const done = nativeProgress.current;
        if (done) {
            // The phone started the timer (and maybe more) while the app was away: take over where it is.
            nativeProgress.current = null;
            for (let i = 0; i < done.completed; i++)
                dispatch(focusCompleted(undefined));
            setTimer((cur) => joinNativeTimer(cur, done, Date.now()));
            return;
        }
        // Still on the focus tomato: no session ended, so there is nothing to continue.
        if (timer.mode !== 'focus') toggle();
    }, [
        startNextWanted,
        timer.status,
        timer.mode,
        celebrating,
        toggle,
        dispatch,
    ]);

    // Stable identities, so the memoized timer buttons do not re-render on every tick.
    const handleMode = useCallback(
        (m: Mode) => {
            playCue('ui');
            setTimer((t) => selectMode(t, m, durations));
        },
        [durations]
    );
    const handleReset = useCallback(() => {
        playCue('pause');
        setTimer((t) => reset(t, durations));
    }, [durations]);
    const handleSkip = useCallback(() => {
        playCue('ui');
        // Skipping never counts as a finished pomodoro.
        setTimer((t) => advance(t, durations, false));
    }, [durations]);

    const preview = useCallback(
        async (kind: CompletionId) => {
            if (celebrating) return;
            setCelebrating(true);
            await tomato.current?.playCompletion(kind);
            setCelebrating(false);
        },
        [celebrating]
    );

    const insets = useMemo(
        () => ({
            bottom: drawerOpen && mobile ? `${SHEET_HEIGHT_VH}vh` : 0,
            right: drawerOpen && !mobile ? DRAWER_WIDTH : 0,
        }),
        [drawerOpen, mobile]
    );

    return (
        <Box
            sx={{
                position: 'relative',
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
                background: backgroundCss(
                    look.background,
                    look.background === 'default' && look.glowColor
                        ? look.glowColor
                        : look.color,
                    dark
                ),
                backgroundSize: backgroundSize(look.background),
                transition: 'background 0.4s',
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    ...insets,
                    transition: 'right .2s, bottom .2s',
                }}
            >
                <Box sx={{ position: 'absolute', inset: 0 }}>
                    <TomatoCanvas
                        ref={tomato}
                        look={look}
                        progress={progress}
                        totalMs={timer.mode === 'roulette' ? 0 : total}
                        running={running && timer.mode === 'focus'}
                        reserveBottom={!(drawerOpen && mobile)}
                        dark={dark}
                    />
                </Box>
            </Box>
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: insets.right,
                    bottom: insets.bottom,
                    pointerEvents: 'none',
                }}
            >
                <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                        position: 'absolute',
                        // Below the transparent header, which the scene fills behind.
                        top: {
                            xs: 'calc(var(--inset-top, env(safe-area-inset-top, 0px)) + 64px)',
                            sm: 'calc(var(--inset-top, env(safe-area-inset-top, 0px)) + 72px)',
                        },
                        left: 12,
                    }}
                >
                    <Chip
                        label={
                            <EmojiText>
                                {t('page.today', { count: stats.today })}
                            </EmojiText>
                        }
                        sx={{ fontWeight: 800 }}
                    />
                    {listening && (
                        <Chip
                            label={<EmojiText>{t('page.listening')}</EmojiText>}
                            onDelete={() => void toggleListening()}
                            sx={{ fontWeight: 800, pointerEvents: 'auto' }}
                        />
                    )}
                    <Chip
                        label={
                            <EmojiText>
                                {shownStreak > 0
                                    ? t('page.streak', { count: shownStreak })
                                    : t('page.streakNone')}
                            </EmojiText>
                        }
                        onClick={() => setStreakOpen(true)}
                        sx={{
                            fontWeight: 800,
                            pointerEvents: 'auto',
                            opacity: shownStreak > 0 ? 1 : 0.75,
                        }}
                    />
                </Stack>

                {popups.map((p) => (
                    <Box
                        key={p.id}
                        sx={{
                            position: 'absolute',
                            left: '50%',
                            top: '38%',
                            fontSize: 44,
                            fontWeight: 800,
                            color: 'text.primary',
                            textShadow: `0 3px 0 ${alpha(look.color, 0.6)}`,
                            animation: `${floatUp} 1.5s ease-out forwards`,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        <EmojiText>{p.text}</EmojiText>
                    </Box>
                ))}

                {!(drawerOpen && mobile) && (
                    <Box
                        sx={{
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            // The phone app has no padding under the page: clear the system bar, and the banner.
                            bottom: isNative()
                                ? `calc(var(--inset-bottom, 0px) + ${nativeBanner ? 60 : 16}px)`
                                : { xs: 16, sm: 28 },
                            pointerEvents: 'auto',
                        }}
                    >
                        <TimerPanel
                            mode={timer.mode}
                            status={timer.status}
                            remainingMs={timer.remainingMs}
                            progress={progress}
                            color={look.color}
                            onDarkScene={isDarkScene(look.background, dark)}
                            celebrating={celebrating}
                            shortMinutes={durations.short}
                            onMode={handleMode}
                            onToggle={toggle}
                            onReset={handleReset}
                            onSkip={handleSkip}
                        />
                    </Box>
                )}
            </Box>

            <CustomizeDrawer
                open={drawerOpen}
                onClose={onCloseDrawer}
                onPreview={(k) => void preview(k)}
                previewing={celebrating}
                footerOffset={footerOffset}
            />
            <StreakDialog
                open={streakOpen}
                onClose={() => setStreakOpen(false)}
                streak={shownStreak}
                status={streakStatus(stats, new Date())}
            />
            <StreakRecoveryDialog
                open={giftOpen}
                streak={stats.lostStreak?.streak ?? 0}
                retry={giftRetry}
                busy={giftBusy}
                onAccept={() => void acceptGift()}
                onDecline={() => dispatch(streakRecoveryDeclined())}
            />
            <SettingsDialog
                open={settingsOpen}
                onClose={onCloseSettings}
                listening={listening}
                listenFailed={listenFailed}
                onToggleListening={() => void toggleListening()}
                onSkipToLast={() => setTimer((t) => skipToLast(t, Date.now()))}
            />
        </Box>
    );
};
